import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter, Inject,
  Input, OnDestroy,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output, PLATFORM_ID,
} from '@angular/core';
import {MediaObjectInterface} from '../../core/interfaces/media-object.interface';
import {MediaTypeEnum} from '../../core/enums/media-type.enum';
import {
  createVideoPlaybackAttempt, getVideoSourceSignature as videoSourceSignature,
  VideoDecoderMode, VideoPlaybackAttempt, VideoPlaybackReason, VideoService, ViewportDecoderHandoff,
} from '../timeline/video.service';
import type * as Plyr from 'plyr';
import {MediaOfflineService} from "../../core/services/media-offline.service";
import {MediaObjectPipe} from "../../core/pipes/media-object.pipe";

@Component({
  selector: 'app-media-view',
  templateUrl: './media-view.component.html',
  styleUrls: ['./media-view.component.scss']
})
export class MediaViewComponent implements OnInit, OnChanges, AfterViewInit, AfterViewChecked, OnDestroy {
  get currentTime(): number {
    return this._currentTime;
  }

  get cssAspectRatio(): string {
    const ratio = Number(this.ratio) || (9 / 16);
    return `${1 / ratio}`;
  }

  get galleryBackdropUrl(): string | null {
    if (this.mediaObject?.type === MediaTypeEnum.VIDEO) {
      return this.getVideoPoster() || null;
    }
    return MediaObjectPipe.transformToLink(this.mediaObject, {formatType: 'thumb_md', defaultImageType: 'loading'});
  }

  get backdropUrl(): string | null {
    if (this.deferLoading || !(this.showBackdrop || this.isInLightBoxContext)) return null;
    const url = this.galleryBackdropUrl;
    return url && url !== this.failedBackdropUrl ? url : null;
  }

  public onBackdropError(url: string): void {
    this.failedBackdropUrl = url;
  }

  @Input() set currentTime(value: number) {
    // Inputs are parent-owned state. Emitting from this setter fed the active
    // slide's time back into every warm neighbour through `[(currentTime)]`,
    // which could seek a prepared decoder away from its 0–4 second buffer.
    this._currentTime = Number.isFinite(value) ? value : 0;
  }

  @Input() public mediaObject: MediaObjectInterface;
  @Input() public id: any;
  @Input() public prefix: string = 'video';
  @Input() public clickable: boolean = false;
  @Input() public contain: boolean = false;
  /** Fill unused media space with a blurred thumbnail, without another player. */
  @Input() public showBackdrop = false;
  @Input() public priority: boolean = false;
  /** Timeline virtualization supplies its own bounded prewarm window. */
  @Input() public prewarm = true;
  /** Static Home swipe preview: render a native thumbnail/poster, never a player. */
  @Input() public thumbnailOnly = false;
  /** Off-screen swipe-preview panels keep their card geometry without decoding media. */
  @Input() public deferLoading = false;
  @Input() public ratio: number = 9/16;
  @Input() public isInLightBoxContext: boolean = false;
  /** The gallery creates a decoder only for its active video slide. */
  @Input() public videoDecoderMode: VideoDecoderMode = 'active';
  /** Transient gallery resources use an instance key so stale modal cleanup is isolated. */
  @Input() public videoResourceKey: string | null = null;
  @Input() public videoResourceScope: string | null = null;
  /** One-use Media-grid decoder transfer, consumed only by the matching gallery slide. */
  @Input() public viewportDecoderHandoff: ViewportDecoderHandoff | null = null;
  public isEnded: boolean = false;
  private _currentTime: number = 0;
  @Output() public currentTimeChange: EventEmitter<number> = new EventEmitter<number>();
  public readonly MediaTypeEnum = MediaTypeEnum;
  public player: Plyr;
  public imageLoaded: boolean = false;
  @Output() playChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  /** Gallery-only playback state, scoped to the resource that emitted it. */
  @Output() public videoLoadingChange = new EventEmitter<{key: string | null; loading: boolean}>();
  @Output() public videoPlaybackError = new EventEmitter<{key: string | null}>();
  /** The gallery owns the single active playback attempt after this player is ready. */
  @Output() public lightboxPlayerReady: EventEmitter<void> = new EventEmitter<void>();
  el: HTMLElement;
  videoKey: string;
  private viewInitialized = false;
  private imageReadyCheckQueued = false;
  private failedBackdropUrl: string | null = null;
  private imageReadyCheckFrameId: number | null = null;
  private lastVideoSourceSignature: string | null = null;
  private videoInitializationVersion = 0;
  private playerInitializationInFlight = false;
  private destroyed = false;
  private activeVideoElement: HTMLVideoElement | null = null;
  private lightboxAutoplayRetryCleanup?: () => void;
  private nativeLightboxPlaybackCleanup?: () => void;
  private lightboxPlaybackVersion = 0;
  private lightboxLoadingTimer?: ReturnType<typeof setTimeout>;
  private pendingVideoActivationFrameId: number | null = null;
  private viewportDecoderTransferred = false;
  private videoFailed = false;
  private pendingVideoRetry = false;
  private automaticVideoRecoveries = 0;
  private playerGeneration = 0;
  private playbackAttempt?: VideoPlaybackAttempt;
  private preferHlsJs = false;
  private hasViewportSelection = false;

  public get playbackGeneration(): number {
    return this.playerGeneration;
  }

  constructor(
    @Inject(PLATFORM_ID) public platformId,
    public readonly elementRef: ElementRef,
    public videoService: VideoService,
    public cdr: ChangeDetectorRef,
    private readonly mediaOffline: MediaOfflineService
  ) {
  }

  async ngOnInit() {
    if (!this.id) {
      this.id = this.mediaObject.id;
    }
    if (this.thumbnailOnly) {
      return;
    }
    this.prewarmCurrentMedia();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mediaObject']) {
      this.updateMediaObject(changes['mediaObject']);
    }
    if (changes['videoDecoderMode'] && !changes['videoDecoderMode'].firstChange && this.isInLightBoxContext) {
      if (this.videoDecoderMode === 'active' && changes['videoDecoderMode'].previousValue !== 'active'
        && !(changes['mediaObject'] && videoSourceSignature(changes['mediaObject'].previousValue) !== this.getVideoSourceSignature())) {
        this.beginPlaybackCycle('selection');
      }
      if (this.videoDecoderMode === 'inactive') {
        this.setVideoLoading(false);
        this.invalidateVideoInitialization();
        this.releaseVideoResources();
      } else if (!this.thumbnailOnly && !this.deferLoading) {
        const videoElement = this.activeVideoElement || this.findVideoElement();
        const videoKey = this.videoKey || this.videoResourceKey || videoElement?.id;
        if (this.videoDecoderMode === 'active' && this.videoFailed) {
          this.recoverVideoPlayback();
        } else if (this.player && videoElement && videoKey) {
          if (this.videoDecoderMode === 'active') {
            this.lightboxPlayerReady.emit();
          } else {
            this.cancelLightboxPlayback();
            if (this.videoDecoderMode === 'warm') {
              try {
                videoElement.currentTime = 0;
              } catch {
                // The HLS metadata transition can still be in flight.
              }
            }
            this.videoService.setVideoDecoderMode(videoKey, videoElement, this.videoDecoderMode);
          }
        } else {
          void this.activateVideoForCurrentContext();
        }
      }
    }
    if (changes['deferLoading'] && !changes['deferLoading'].firstChange) {
      if (this.deferLoading) {
        this.invalidateVideoInitialization();
        this.releaseVideoResources();
      } else {
        this.scheduleImageReadyCheck();
        if (!this.thumbnailOnly) {
          this.activateFromThumbnail();
        }
      }
    }
    if (
      changes['thumbnailOnly']
      && !changes['thumbnailOnly'].firstChange
      && this.thumbnailOnly
    ) {
      this.invalidateVideoInitialization();
      this.releaseVideoResources();
    }
    if (
      changes['thumbnailOnly']
      && !changes['thumbnailOnly'].firstChange
      && !this.thumbnailOnly
    ) {
      this.activateFromThumbnail();
    }
  }

  private updateMediaObject(change: SimpleChanges[string]): void {
    const previous = change.previousValue as MediaObjectInterface | undefined;
    const nextSignature = this.getVideoSourceSignature();
    const previousSignature = this.lastVideoSourceSignature ?? videoSourceSignature(previous);
    const sameVideo = previous?.type === MediaTypeEnum.VIDEO && this.mediaObject?.type === MediaTypeEnum.VIDEO
      && previousSignature === nextSignature;
    this.lastVideoSourceSignature = nextSignature;
    if (sameVideo) {
      // Metadata/live-list refreshes do not invalidate an in-flight decoder or a failed attempt.
      return;
    }
    if (!change.firstChange) {
      this.pendingVideoRetry = this.playerInitializationInFlight;
      this.invalidateVideoInitialization();
      this.releaseVideoResources();
    }
    this.preferHlsJs = false;
    this.videoFailed = false;
    this.isEnded = false;
    this.beginPlaybackCycle(previous ? 'source-change' : 'selection');
    this.imageLoaded = false;
    this.scheduleImageReadyCheck();
    if (!this.thumbnailOnly) {
      this.prewarmCurrentMedia();
      if (this.viewInitialized && this.mediaObject?.type === MediaTypeEnum.VIDEO && this.hasPlayableVideoSource()) {
        this.scheduleVideoActivationAfterRender();
      }
    }
  }

  private beginPlaybackCycle(reason: Exclude<VideoPlaybackReason, 'recovery'>): void {
    this.automaticVideoRecoveries = 0;
    this.beginPlaybackAttempt(reason);
  }

  private beginPlaybackAttempt(reason: VideoPlaybackReason): void {
    if (this.playbackAttempt) {
      this.playbackAttempt.retired = true;
    }
    this.playbackAttempt = createVideoPlaybackAttempt(reason);
    if (this.videoKey && this.activeVideoElement) {
      this.videoService.setPlaybackAttempt?.(this.videoKey, this.activeVideoElement, this.playbackAttempt);
    }
  }

  private activateFromThumbnail(): void {
    this.prewarmCurrentMedia();
    this.scheduleImageReadyCheck();
    if (this.viewInitialized && this.mediaObject?.type === MediaTypeEnum.VIDEO && this.hasPlayableVideoSource() && !this.player) {
      this.lastVideoSourceSignature = this.getVideoSourceSignature();
      this.scheduleVideoActivationAfterRender();
    }
  }

  async ngAfterViewInit() {
    this.viewInitialized = true;
    if (this.deferLoading) {
      return;
    }
    this.scheduleImageReadyCheck();
    if (this.thumbnailOnly) {
      return;
    }
    if (this.mediaObject.type === MediaTypeEnum.VIDEO && this.hasPlayableVideoSource()) {
      this.lastVideoSourceSignature = this.getVideoSourceSignature();
      await this.activateVideoForCurrentContext();
    } else if (this.mediaObject.type === MediaTypeEnum.VIDEO && this.isInLightBoxContext) {
      this.reportVideoError();
    }
  }

  private async activateVideoForCurrentContext(): Promise<void> {
    if (this.thumbnailOnly || this.deferLoading || this.destroyed || this.videoFailed) {
      return;
    }
    if (!this.hasPlayableVideoSource()) {
      if (this.isInLightBoxContext && this.videoDecoderMode === 'active') {
        this.reportVideoError();
      }
      return;
    }
    let videoElement = this.findVideoElement();
    if (!videoElement) {
      return;
    }
    videoElement = this.adoptViewportDecoderHandoff(videoElement);
    this.videoKey = videoElement.id;
    this.activeVideoElement = videoElement;
    if (this.isInLightBoxContext) {
      if (this.videoDecoderMode !== 'inactive') {
        await this.initPlayer(this.videoDecoderMode);
      }
      return;
    }
    // Timeline playback used to initialise every card while it was mounted
    // and only then register it for viewport-based autoplay.  Keep that
    // ordering: delaying source setup until a card becomes active makes the
    // initial play race HLS attachment and is noticeably unreliable.
    await this.initializeTimelineVideo(videoElement);
  }

  /**
   * Promoting a swipe preview changes an *ngIf from poster to <video>. Inputs
   * are updated before that embedded view exists, so looking up the element in
   * ngOnChanges loses the only initialization attempt. Run once in the next
   * frame, after Angular has committed the video node, and invalidate it with
   * the same generation used by async player creation.
   */
  private scheduleVideoActivationAfterRender(): void {
    if (
      this.pendingVideoActivationFrameId !== null
      || !this.viewInitialized
      || this.destroyed
      || this.thumbnailOnly
      || this.deferLoading
      || this.player
      || this.playerInitializationInFlight
      || this.videoFailed
      || this.mediaObject?.type !== MediaTypeEnum.VIDEO
      || !this.hasPlayableVideoSource()
    ) {
      return;
    }

    const initializationVersion = this.videoInitializationVersion;
    const activate = () => {
      this.pendingVideoActivationFrameId = null;
      if (
        initializationVersion !== this.videoInitializationVersion
        || this.destroyed
        || this.thumbnailOnly
        || this.deferLoading
        || this.player
        || this.playerInitializationInFlight
        || this.videoFailed
        || this.mediaObject?.type !== MediaTypeEnum.VIDEO
        || !this.hasPlayableVideoSource()
      ) {
        return;
      }
      void this.activateVideoForCurrentContext();
    };

    if (typeof requestAnimationFrame === 'function') {
      this.pendingVideoActivationFrameId = requestAnimationFrame(activate);
      return;
    }

    // SSR does not provide requestAnimationFrame. The generation guard above
    // keeps this fallback harmless if the component changes before it runs.
    Promise.resolve().then(activate);
  }

  private async initializeTimelineVideo(videoElement: HTMLVideoElement): Promise<void> {
    const initializationVersion = this.videoInitializationVersion;
    await this.initPlayer('active');

    if (!this.isVideoInitializationCurrent(initializationVersion, videoElement) || !this.player) {
      return;
    }

    this.videoService.registerTimelineVideo(
      videoElement,
      () => this.releaseTimelineDecoder(),
    );
  }

  /**
   * The Media grid has already attached HLS and filled this element's short
   * startup buffer. Replace Angular's just-rendered gallery node before
   * `initPlayer()` so the player UI is the only newly-created layer.
   */
  private adoptViewportDecoderHandoff(templateVideo: HTMLVideoElement): HTMLVideoElement {
    if (!this.isInLightBoxContext || !this.viewportDecoderHandoff || this.mediaObject?.type !== MediaTypeEnum.VIDEO) {
      return templateVideo;
    }
    const resourceKey = this.videoResourceKey || templateVideo.id;
    const adopted = (this.videoService as VideoService & {adoptViewportWarmDecoder?: VideoService['adoptViewportWarmDecoder']}).adoptViewportWarmDecoder?.(
      this.viewportDecoderHandoff,
      this.mediaObject.id,
      resourceKey,
      this.videoResourceScope,
      templateVideo,
    );
    if (adopted) {
      this.viewportDecoderHandoff = null;
      return adopted;
    }
    return templateVideo;
  }

  ngAfterViewChecked(): void {
    if (this.deferLoading) {
      return;
    }
    this.scheduleImageReadyCheck();
  }

  initClickVideoEvent(): void {
    this.el = this.elementRef.nativeElement.querySelector(`#${this.prefix}-${this.id || this.mediaObject.id} + .plyr__poster`);
    this.el.addEventListener('click', this.videoClickHandler, {passive: true});
  }

  togglePlay(toggle?: {value?: boolean, event?: Event}): void {
    if (toggle?.event) {
      toggle.event.stopPropagation();
    }
    if (this.videoFailed) {
      void this.retryLightboxPlayback();
      return;
    }

    const videoElement = this.findVideoElement();
    if (videoElement) {
      videoElement.muted = this.videoService.muted;
      const shouldPlay = typeof toggle?.value === 'boolean' ? toggle.value : videoElement.paused;
      if (shouldPlay) {
        const playResult = videoElement.play();
        if (playResult && typeof (playResult as Promise<void>).catch === 'function') {
          (playResult as Promise<void>).catch(() => {});
        }
      } else {
        videoElement.pause();
      }
      return;
    }

    if (this.player) {
      this.applyMutedState(this.videoService.muted, null);
      try {
        this.player.togglePlay(toggle?.value);
      } catch {
        // No native element is currently attached; ignore the click rather than throwing.
      }
    }
  }

  public hasControllablePlayer(): boolean {
    return !!this.player && !!this.findVideoElement();
  }

  public isPlaybackPaused(): boolean {
    const videoElement = this.findVideoElement();
    if (videoElement) {
      return videoElement.paused;
    }

    if (!this.player) {
      return true;
    }

    try {
      return this.player.paused;
    } catch {
      return true;
    }
  }

  /** Exposes the currently owned native node for the gallery's close-only handoff. */
  public getActiveVideoElement(): HTMLVideoElement | null {
    return this.activeVideoElement || this.findVideoElement();
  }

  public getPlaybackCurrentTime(): number {
    const videoElement = this.findVideoElement();
    if (videoElement) {
      return videoElement.currentTime || this._currentTime || 0;
    }

    if (!this.player) {
      return this._currentTime || 0;
    }

    try {
      return this.player.currentTime || this._currentTime || 0;
    } catch {
      return this._currentTime || 0;
    }
  }

  public resumePlaybackFrom(currentTime: number, muted: boolean): void {
    this.currentTime = currentTime;

    const videoElement = this.findVideoElement();
    if (videoElement) {
      videoElement.currentTime = currentTime;
      videoElement.muted = muted;
    }

    if (!this.player) {
      return;
    }

    try {
      this.player.currentTime = currentTime;
    } catch {
      // Fall back to the native element time set above when Plyr no longer exposes media.
    }

    this.applyMutedState(muted, videoElement);
    try {
      const playResult = this.player.play();
      if (playResult && typeof (playResult as Promise<void>).catch === 'function') {
        (playResult as Promise<void>).catch(() => {});
      }
    } catch {
      if (videoElement) {
        const nativePlayResult = videoElement.play();
        if (nativePlayResult && typeof (nativePlayResult as Promise<void>).catch === 'function') {
          (nativePlayResult as Promise<void>).catch(() => {});
        }
      }
    }
  }

  async initPlayer(mode: Exclude<VideoDecoderMode, 'inactive'> = 'active'): Promise<void> {
    if (this.player || this.playerInitializationInFlight || this.thumbnailOnly || this.deferLoading || this.destroyed) {
      return;
    }
    const videoElement = this.findVideoElement();
    if (videoElement) {
      this.videoFailed = false;
      if (!this.playbackAttempt || this.playbackAttempt.retired) {
        this.beginPlaybackAttempt('selection');
      }
      const videoKey = this.videoResourceKey || videoElement.id;
      this.videoKey = videoKey;
      this.activeVideoElement = videoElement;
      const initializationVersion = this.videoInitializationVersion;
      this.playerGeneration++;
      videoElement.currentTime = this.isInLightBoxContext && mode === 'warm' ? 0 : this._currentTime;
      this.playerInitializationInFlight = true;
      const hasCurrentFrame = () => !this.videoFailed
        && this.isVideoInitializationCurrent(initializationVersion, videoElement)
        && videoElement.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;
      const readyCallback = () => {
        if (!hasCurrentFrame()) {
          return;
        }
        this.setVideoLoading(false);
      };
      const playingCallback = () => {
        if (!hasCurrentFrame() || videoElement.paused || videoElement.ended) {
          return;
        }
        if (this.isEnded) {
          this.isEnded = false;
          this.cdr.markForCheck();
        }
        readyCallback();
      };
      const waitingCallback = () => {
        if (this.isVideoInitializationCurrent(initializationVersion, videoElement)
          && this.isInLightBoxContext && this.videoDecoderMode === 'active') {
          this.videoService.resumeVideoDecoderAfterWaiting(videoKey, videoElement);
          if (!this.hasBufferedPlayback(videoElement, 0.1)) {
            this.setVideoLoading(true);
          }
        }
      };
      const errorCallback = (event?: Event) => {
        const attempt = this.playbackAttempt;
        if (!this.isVideoInitializationCurrent(initializationVersion, videoElement)
          || !attempt || attempt.retired || attempt.failureHandled) {
          return;
        }
        const sourceKind = this.videoService.getVideoSourceKind?.(videoKey, videoElement);
        if (event?.type === 'error' && (!videoElement.error
          || this.videoService.getVideoSourceKind && !sourceKind)) {
          // A queued native teardown error is not a failure of the new source.
          return;
        }
        attempt.failureHandled = true;
        if (videoElement.error?.code === 4 && sourceKind === 'native-hls' && this.videoService.canUseHlsJs?.()) {
          this.preferHlsJs = true;
        }
        this.videoService.recordPlaybackDiagnostic?.(videoKey, videoElement, 'media-error', attempt);
        this.videoService.consumeViewportDecoderFallback?.(videoKey, videoElement);
        console.warn('[Video] media failed', videoKey, attempt.id, attempt.reason, sourceKind || 'unknown',
          videoElement.error?.code || 0, videoElement.readyState, videoElement.networkState);
        if (!this.recoverVideoPlayback()) {
          this.reportVideoError();
        }
      };
      // Source attachment can fail before the optional Plyr UI has loaded.
      this.bindNativeLightboxPlaybackSignals(videoElement, playingCallback, readyCallback, waitingCallback, errorCallback);
      if (this.isInLightBoxContext && mode === 'active') {
        this.scheduleVideoLoadingIndicator(this.lightboxPlaybackVersion);
      }
      try {
        const player = await this.videoService.initPlayer(
          videoElement,
          this.mediaObject,
          () => {
            if (this.isVideoInitializationCurrent(initializationVersion, videoElement) && this.clickable) {
              this.initClickVideoEvent();
            }
          },
          () => this.isVideoInitializationCurrent(initializationVersion, videoElement),
          {
            mode,
            resourceKey: this.videoResourceKey || undefined,
            scope: this.videoResourceScope || undefined,
            // A warm gallery neighbour uses a muted technical play to decode
            // one frame. Only the selected, active slide may pin the complete
            // stream for offline use.
            cacheForOfflineOnPlay: !this.isInLightBoxContext || mode === 'active',
            startPlaybackImmediately: this.isInLightBoxContext && mode === 'active',
            preferHlsJs: this.preferHlsJs,
            attempt: this.playbackAttempt,
          }
        );

        if (!player || !this.isVideoInitializationCurrent(initializationVersion, videoElement)) {
          if (this.viewportDecoderTransferred) {
            return;
          }
          if (this.destroyed) {
            this.videoService.disposeVideo(videoKey, videoElement);
          } else {
            this.videoService.releaseVideoDecoder(videoKey, videoElement);
          }
          return;
        }

        this.player = player;

        this.videoService.eventListeners.push({key: videoKey, value: {key: 'playing', value: playingCallback}});
        this.player.on('playing', playingCallback);
        this.videoService.eventListeners.push({key: videoKey, value: {key: 'waiting', value: waitingCallback}});
        this.player.on('waiting', waitingCallback);
        this.videoService.eventListeners.push({key: videoKey, value: {key: 'error', value: errorCallback}});
        this.player.on('error', errorCallback);

        const timeupdateCallback = () => {
          this._currentTime = videoElement.currentTime;
          this.currentTimeChange.emit(this._currentTime);
        };
        this.videoService.eventListeners.push({key: videoKey, value: {key: 'timeupdate', value: timeupdateCallback}});
        this.player.on('timeupdate', timeupdateCallback);

        const endedCallback = () => {
          if (this.videoFailed || !this.isVideoInitializationCurrent(initializationVersion, videoElement)
            || this.player !== player) {
            return;
          }
          this.isEnded = true;
          player.stop();
          this.cdr.markForCheck();
        };
        this.videoService.eventListeners.push({key: videoKey, value: {key: 'ended', value: endedCallback}});
        this.player.on('ended', endedCallback);

        // Bind every readiness signal before an active decoder is promoted.
        // A cached/adopted MSE pipeline can reach `playing` in the same task;
        // registering after play leaves the modal loader visible forever.
        this.bindNativeLightboxPlaybackSignals(videoElement, playingCallback, readyCallback, waitingCallback, errorCallback);
        if (videoElement.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          readyCallback();
        }

        if (this.isInLightBoxContext) {
          // This component may have started while it was a warm neighbour and
          // become the active slide before async Plyr/HLS setup completed.
          // Use the current input, not the stale `mode` captured at start;
          // otherwise HLS remains stopped after the manifest and the video can
          // appear to end after its initial buffered portion.
          const currentMode = this.videoDecoderMode;
          if (currentMode === 'active') {
            this.lightboxPlayerReady.emit();
          } else {
            this.videoService.setVideoDecoderMode(videoKey, videoElement, currentMode);
          }
        }

      } catch (error) {
        if (this.isVideoInitializationCurrent(initializationVersion, videoElement)) {
          console.warn('[Video] initialization failed', videoKey, initializationVersion,
            error instanceof Error ? error.name : 'unknown');
          errorCallback();
        }
      } finally {
        this.playerInitializationInFlight = false;
        if (this.pendingVideoRetry && !this.destroyed) {
          this.pendingVideoRetry = false;
          this.scheduleVideoActivationAfterRender();
        }
      }
    }
  }

  videoClickHandler(): void {
      this.togglePlay();
  }

  toggleVolume(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.videoService.setMuted(!this.videoService.muted);
  }

  ngOnDestroy(): void {
    // If the matching virtual slide never mounted, release the claimed grid
    // decoder rather than retaining it in the temporary transfer host.
    (this.videoService as VideoService & {disposeViewportDecoderHandoff?: VideoService['disposeViewportDecoderHandoff']})
      .disposeViewportDecoderHandoff?.(this.viewportDecoderHandoff);
    this.viewportDecoderHandoff = null;
    this.destroyed = true;
    this.elementRef.nativeElement.removeAllListeners?.();
    this.releaseMediaResources();
  }

  /**
   * Gallery teardown can run before Angular removes the dynamic view. Releasing
   * the player here prevents the closing dialog animation from retaining a
   * decoder or a native video source. It is intentionally idempotent.
   */
  public releaseMediaResources(): void {
    this.invalidateVideoInitialization();
    this.cancelImageReadyCheck();
    if (this.viewportDecoderTransferred) {
      return;
    }
    this.releaseVideoResources();
  }

  /**
   * The gallery hands the active Media-grid pipeline back to its viewport
   * pool before Angular destroys this slide. Never let this dying component
   * subsequently clear the transferred native source.
   */
  public detachViewportDecoderForReturn(): {videoKey: string; element: HTMLVideoElement} | null {
    const element = this.activeVideoElement || this.findVideoElement();
    const videoKey = this.videoKey || this.videoResourceKey || element?.id;
    if (!element || !videoKey || !this.isInLightBoxContext || this.playerInitializationInFlight) {
      return null;
    }
    this.viewportDecoderTransferred = true;
    this.invalidateVideoInitialization();
    this.el?.removeEventListener('click', this.videoClickHandler);
    this.nativeLightboxPlaybackCleanup?.();
    this.nativeLightboxPlaybackCleanup = undefined;
    this.activeVideoElement = null;
    this.player = undefined;
    this.videoKey = undefined;
    this.viewportDecoderHandoff = null;
    return {videoKey, element};
  }

  private invalidateVideoInitialization(): void {
    if (this.playbackAttempt) {
      this.playbackAttempt.retired = true;
    }
    this.videoInitializationVersion += 1;
    this.cancelLightboxPlayback();
    if (this.pendingVideoActivationFrameId !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.pendingVideoActivationFrameId);
      this.pendingVideoActivationFrameId = null;
    }
  }

  private releaseTimelineDecoder(): void {
    this.invalidateVideoInitialization();
    this.el?.removeEventListener('click', this.videoClickHandler);
    this.player = undefined;
  }

  private playLightboxVideo(videoElement: HTMLVideoElement, allowRetry: boolean = true): void {
    this.clearLightboxAutoplayRetry();
    const playbackVersion = this.lightboxPlaybackVersion;
    const attempt = this.playbackAttempt;
    const onRejected = (error: unknown) => {
      if (playbackVersion !== this.lightboxPlaybackVersion || this.destroyed || this.activeVideoElement !== videoElement
        || attempt?.retired || this.playbackAttempt !== attempt) {
        return;
      }
      this.mediaOffline.setPlaybackWaiting?.(videoElement, false);
      const name = typeof error === 'object' && error ? String((error as {name?: string}).name || 'unknown') : 'unknown';
      this.videoService.recordPlaybackDiagnostic?.(this.videoKey, videoElement, `play-rejected:${name}`, attempt);
      console.warn('[Video] play rejected', this.videoKey, attempt?.id, playbackVersion, name);
      if (name !== 'NotAllowedError' && allowRetry && this.videoDecoderMode === 'active') {
        this.bindLightboxAutoplayRetry(videoElement);
      }
    };
    videoElement.muted = this.videoService.muted;
    try {
      const result = this.player?.play() || videoElement.play();
      if (result && typeof (result as Promise<void>).catch === 'function') {
        void (result as Promise<void>).catch(onRejected);
      }
    } catch (error) {
      onRejected(error);
    }
  }

  /** Pause without leaving a pending autoplay retry behind an overlaid dialog. */
  public pauseLightboxPlayback(): void {
    this.cancelLightboxPlayback();
    (this.activeVideoElement || this.findVideoElement())?.pause();
  }

  /** Called by the gallery after Swiper selects this slide. */
  public activateLightboxPlayback(): boolean {
    const videoElement = this.activeVideoElement || this.findVideoElement();
    const videoKey = this.videoKey || this.videoResourceKey || videoElement?.id;
    if (this.videoFailed || !this.isInLightBoxContext || !videoElement || !videoKey || this.videoDecoderMode !== 'active') {
      return false;
    }

    const playbackVersion = ++this.lightboxPlaybackVersion;
    if (!this.videoService.isVideoDecoderPlaybackReady(videoKey, videoElement)) {
      this.scheduleVideoLoadingIndicator(playbackVersion);
    }
    this.videoService.cacheMediaForOfflineOnPlay(videoElement, this.mediaObject);
    this.clearLightboxAutoplayRetry();
    // Native play queues against MSE while HLS fills. Waiting for a separate
    // `FRAG_BUFFERED` promise here adds a full scheduler turn even for an
    // already-warm decoder, so promotion and play intentionally happen in the
    // same task. `waiting` still resumes HLS and controls the loader.
    this.videoService.setVideoDecoderMode(videoKey, videoElement, 'active');
    if (
      playbackVersion === this.lightboxPlaybackVersion
      && !this.destroyed
      && this.videoDecoderMode === 'active'
      && this.activeVideoElement === videoElement
    ) {
      this.playLightboxVideo(videoElement);
      return true;
    }
    return false;
  }

  /** One cold rebuild per activation; both native and HLS errors use this path. */
  private recoverVideoPlayback(): boolean {
    if (this.destroyed || this.isInLightBoxContext && this.videoDecoderMode !== 'active'
      || this.automaticVideoRecoveries >= 1 || !this.hasPlayableVideoSource()) {
      return false;
    }
    this.automaticVideoRecoveries++;
    console.info('[Video] rebuilding decoder', this.videoKey, this.playerGeneration, this.automaticVideoRecoveries);
    this.pendingVideoRetry = this.playerInitializationInFlight;
    this.invalidateVideoInitialization();
    this.releaseVideoResources();
    this.beginPlaybackAttempt('recovery');
    this.videoFailed = false;
    this.setVideoLoading(true);
    if (!this.pendingVideoRetry) {
      this.scheduleVideoActivationAfterRender();
    }
    return true;
  }

  private cancelLightboxPlayback(): void {
    this.lightboxPlaybackVersion += 1;
    this.clearVideoLoadingIndicator();
    this.clearLightboxAutoplayRetry();
  }

  private bindLightboxAutoplayRetry(videoElement: HTMLVideoElement): void {
    this.clearLightboxAutoplayRetry();
    const retry = () => {
      this.clearLightboxAutoplayRetry();
      if (!this.destroyed && this.videoDecoderMode === 'active' && this.activeVideoElement === videoElement) {
        this.playLightboxVideo(videoElement, false);
      }
    };
    videoElement.addEventListener('loadedmetadata', retry, {once: true});
    videoElement.addEventListener('canplay', retry, {once: true});
    const timer = setTimeout(retry, 120);
    this.lightboxAutoplayRetryCleanup = () => {
      clearTimeout(timer);
      videoElement.removeEventListener('loadedmetadata', retry);
      videoElement.removeEventListener('canplay', retry);
    };
  }

  private clearLightboxAutoplayRetry(): void {
    this.lightboxAutoplayRetryCleanup?.();
    this.lightboxAutoplayRetryCleanup = undefined;
  }

  private bindNativeLightboxPlaybackSignals(
    videoElement: HTMLVideoElement,
    playing: () => void,
    ready: () => void,
    waiting: () => void,
    error: () => void,
  ): void {
    this.nativeLightboxPlaybackCleanup?.();
    const activated = () => {
      if (!this.hasViewportSelection) {
        this.hasViewportSelection = true;
        if (this.playbackAttempt && !this.playbackAttempt.retired) {
          // Preparing the first source may already have spent its recovery. Selection
          // must not grant the same source a second cold rebuild immediately afterwards.
          this.videoService.recordPlaybackDiagnostic?.(this.videoKey, videoElement, 'selection', this.playbackAttempt);
          return;
        }
      }
      this.beginPlaybackCycle('selection');
    };
    videoElement.addEventListener('wtg-video-activate', activated);
    videoElement.addEventListener('playing', playing);
    videoElement.addEventListener('loadeddata', ready);
    videoElement.addEventListener('canplay', ready);
    videoElement.addEventListener('timeupdate', ready);
    videoElement.addEventListener('waiting', waiting);
    videoElement.addEventListener('error', error);
    videoElement.addEventListener('wtg-video-error', error);
    this.nativeLightboxPlaybackCleanup = () => {
      videoElement.removeEventListener('wtg-video-activate', activated);
      videoElement.removeEventListener('playing', playing);
      videoElement.removeEventListener('loadeddata', ready);
      videoElement.removeEventListener('canplay', ready);
      videoElement.removeEventListener('timeupdate', ready);
      videoElement.removeEventListener('waiting', waiting);
      videoElement.removeEventListener('error', error);
      videoElement.removeEventListener('wtg-video-error', error);
    };
  }

  private reportVideoError(): void {
    const key = this.videoResourceKey || this.videoKey || null;
    this.videoFailed = true;
    this.pendingVideoRetry = false;
    this.invalidateVideoInitialization();
    this.releaseVideoResources();
    if (this.isInLightBoxContext) {
      this.videoPlaybackError.emit({key});
    }
    this.setVideoLoading(false);
  }

  public async retryLightboxPlayback(): Promise<void> {
    if (this.destroyed) {
      return;
    }
    this.pendingVideoRetry = this.playerInitializationInFlight;
    this.invalidateVideoInitialization();
    this.releaseVideoResources();
    this.beginPlaybackCycle('manual-retry');
    this.videoFailed = false;
    if (!this.hasPlayableVideoSource()) {
      this.reportVideoError();
      return;
    }
    if (!this.pendingVideoRetry) {
      await this.activateVideoForCurrentContext();
    }
  }

  private setVideoLoading(loading: boolean): void {
    if (this.isInLightBoxContext) {
      if (!loading) {
        this.clearVideoLoadingIndicator();
      }
      this.videoLoadingChange.emit({
        key: this.videoKey || this.videoResourceKey || this.activeVideoElement?.id || null,
        loading,
      });
    }
  }

  private scheduleVideoLoadingIndicator(playbackVersion: number): void {
    this.clearVideoLoadingIndicator();
    this.lightboxLoadingTimer = setTimeout(() => {
      this.lightboxLoadingTimer = undefined;
      if (
        !this.destroyed
        && playbackVersion === this.lightboxPlaybackVersion
        && this.videoDecoderMode === 'active'
        && !this.hasBufferedPlayback(this.activeVideoElement, 0)
      ) {
        this.setVideoLoading(true);
      }
    // Cache hits still need one MSE append and a browser decode task. Showing
    // a spinner at 180 ms made that normal, local transition look like a
    // network load on nearly every swipe. Keep the poster/frame visible for a
    // short grace period; a genuinely cold or slow stream still reports its
    // waiting state promptly after this threshold.
    }, 300);
  }

  /** A visible decoded frame is enough to remove the initial overlay spinner. */
  private hasBufferedPlayback(videoElement: HTMLVideoElement | null | undefined, aheadSeconds: number): boolean {
    if (!videoElement) {
      return false;
    }
    if (aheadSeconds <= 0 && videoElement.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      return true;
    }
    const position = Number.isFinite(videoElement.currentTime) ? videoElement.currentTime : 0;
    try {
      for (let index = 0; index < videoElement.buffered.length; index += 1) {
        if (videoElement.buffered.start(index) <= position && videoElement.buffered.end(index) >= position + aheadSeconds) {
          return true;
        }
      }
    } catch {
      // The media element can be detached while a virtual slide is removed.
    }
    return false;
  }

  private clearVideoLoadingIndicator(): void {
    if (this.lightboxLoadingTimer) {
      clearTimeout(this.lightboxLoadingTimer);
      this.lightboxLoadingTimer = undefined;
    }
  }

  private isVideoInitializationCurrent(initializationVersion: number, videoElement: HTMLVideoElement): boolean {
    return !this.destroyed
      && !this.thumbnailOnly
      && initializationVersion === this.videoInitializationVersion
      && this.activeVideoElement === videoElement;
  }

  private releaseVideoResources(): void {
    this.clearLightboxAutoplayRetry();
    this.nativeLightboxPlaybackCleanup?.();
    this.nativeLightboxPlaybackCleanup = undefined;
    this.el?.removeEventListener('click', this.videoClickHandler);
    const videoElement = this.activeVideoElement || this.findVideoElement();
    const videoKey = this.videoKey || videoElement?.id;
    this.videoService.disposeVideo(videoKey, videoElement);
    this.activeVideoElement = null;
    this.player = undefined;
    this.videoKey = undefined;
  }

  private prewarmCurrentMedia(): void {
    if (!this.prewarm || !this.mediaObject) {
      return;
    }
    if (this.mediaObject.type === MediaTypeEnum.VIDEO) {
      if (!this.hasPlayableVideoSource()) {
        return;
      }
      const poster = MediaObjectPipe.transformToLink(this.mediaObject, {formatType: 'm3u8_timeline'});
      const playlist = MediaObjectPipe.transformToLink(this.mediaObject, {formatType: 'm3u8'});
      this.mediaOffline.prewarmUrls([poster], 'image', 1);
      this.mediaOffline.prewarmUrls([playlist], 'playlist', 1);
      return;
    }

    const image = MediaObjectPipe.transformToLink(this.mediaObject, {formatType: 'thumb_md', defaultImageType: 'background'});
    this.mediaOffline.prewarmUrls([image], 'image', 1);
  }

  public shouldUseNativeSrc(url: string | null | undefined): boolean {
    if (!url) {
      return false;
    }

    return url.startsWith('blob:') || url.startsWith('data:');
  }

  public onImageLoaded(): void {
    this.imageLoaded = true;
    this.cdr.markForCheck();
  }

  /**
   * Swipe previews retain only a few priority cards. Start their posters while
   * the panel is still entering the viewport; all other thumbnails stay lazy.
   */
  public getThumbnailLoadingMode(): 'eager' | 'lazy' {
    return this.priority ? 'eager' : 'lazy';
  }

  public hasPlayableVideoSource(): boolean {
    const links = Array.isArray(this.mediaObject?.hyperlinks) ? this.mediaObject.hyperlinks : [];
    return links.some((link: any) => (link?.name === 'm3u8' || link?.name === 'original') && !!link?.uri);
  }

  public getVideoPoster(): string {
    return MediaObjectPipe.transformToLink(this.mediaObject, {formatType: 'm3u8_timeline', defaultImageType: 'loading'}) || '';
  }

  public getImageThumbnail(): string {
    return MediaObjectPipe.transformToLink(this.mediaObject, {formatType: 'thumb_md', defaultImageType: 'background'}) || '';
  }

  private getVideoSourceSignature(): string | null {
    return videoSourceSignature(this.mediaObject);
  }

  private findVideoElement(): HTMLVideoElement | null {
    const mediaId = this.id || this.mediaObject?.id;
    if (!mediaId) {
      return null;
    }
    const videoId = `${this.prefix}-${mediaId}`;
    const candidates = Array.from(
      this.elementRef.nativeElement.querySelectorAll?.('video.timeline-player') || []
    ) as HTMLVideoElement[];

    return candidates.find((element) => element.id === videoId && this.isHtmlVideoElement(element)) || null;
  }

  private isHtmlVideoElement(element: Element | null | undefined): element is HTMLVideoElement {
    return !!element
      && element.tagName?.toLowerCase() === 'video'
      && typeof (element as HTMLVideoElement).canPlayType === 'function';
  }

  private applyMutedState(muted: boolean, videoElement: HTMLVideoElement | null): void {
    if (videoElement) {
      videoElement.muted = muted;
    }

    if (!this.player) {
      return;
    }

    try {
      this.player.muted = muted;
    } catch {
      // Plyr can outlive its media node during timeline re-renders; native element mute is enough in that case.
    }
  }

  private scheduleImageReadyCheck(): void {
    const isImage = this.mediaObject?.type === MediaTypeEnum.IMAGE;
    const isThumbnailPoster = this.thumbnailOnly && this.mediaObject?.type === MediaTypeEnum.VIDEO;
    if (this.imageReadyCheckQueued || this.imageLoaded || (!isImage && !isThumbnailPoster)) {
      return;
    }

    if (typeof requestAnimationFrame !== 'function') {
      return;
    }

    this.imageReadyCheckQueued = true;
    this.imageReadyCheckFrameId = requestAnimationFrame(() => {
      this.imageReadyCheckFrameId = null;
      this.imageReadyCheckQueued = false;
      if (this.destroyed) {
        return;
      }
      const imageElement = this.elementRef.nativeElement.querySelector('.media-view-image') as HTMLImageElement | null;
      if (!imageElement) {
        return;
      }

      if (imageElement.complete && imageElement.naturalWidth > 0) {
        this.onImageLoaded();
      }
    });
  }

  private cancelImageReadyCheck(): void {
    if (this.imageReadyCheckFrameId !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.imageReadyCheckFrameId);
    }
    this.imageReadyCheckFrameId = null;
    this.imageReadyCheckQueued = false;
  }
}
